/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IApplicationCustomVariables,
  IBusinessCustomVariables,
  IPersonCustomVariables,
} from 'app/interfaces/customVariablesInterfaces';
import { IPixKey } from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { TCustodianRequest } from 'modules/products/interfaces/custodian';
import { TApplicationSplitBeneficiaryAccount } from '../api/applicationsHttpService';
import { TBeneficiaryAccount } from 'modules/products/interfaces/beneficiaryAccount';

export interface TBorrowerQualificationRequestType {
  id: string;
  funding: Employer;
  employer: Employer;
  role: string;
  status: string;
  borrower: Borrower;
  originator?: string;
  condition?: Condition;
  created_at: string;
  updated_at: string;
}

export interface TBorrowerLimitsRequestType {
  id: string;
  funding: Employer;
  employer: Employer;
  role: string;
  status: string;
  borrower: Borrower;
  condition?: Condition;
  created_at: string;
  updated_at: string;
}

export interface IList<T> {
  content: T[];
  has_next?: boolean;
  total_elements?: number;
  total_pages?: number;
}
export interface Borrower {
  id: string;
  name: string;
}

export type TScheduleBasedConditions = {
  interest_rate: number;
  upper_limit: number;
};

export interface Condition {
  max_payment_number: null | undefined;
  min_payment_number: null | undefined;
  tac: null | undefined;
  max_grace_period: null | undefined;
  min_grace_period: null | undefined;
  min_requested_amount: null | undefined;
  schedule_based_conditions: TScheduleBasedConditions[];
  exp_date: null | undefined;
  line_of_credit?: LineOfCredit;
  due_day: null | undefined;
}

export interface IConditionUpdate {
  max_payment_number: number | undefined;
  min_payment_number: number | undefined;
  tac: number | undefined;
  max_grace_period: number | undefined;
  min_grace_period: number | undefined;
  min_requested_amount: number | undefined;
  schedule_based_conditions: Partial<TScheduleBasedConditions>[];
  exp_date?: string | null;
  line_of_credit?: LineOfCredit;
  due_day: number | undefined;
}

export interface LineOfCredit {
  credit_limit?: number;
  max_payment_amount?: number;
  consumed_payment_amount?: number;
  consumed_credit_limit?: number;
}

export interface Employer {
  id: string;
  legal_name: string;
}

export type TRatingsScheduleBasedConditions = {
  interest_rate: number;
  upper_limit: number;
};

export interface IRatings {
  identifier: string;
  description: string;
  display_name: string;
  max_payment_number: null | number;
  min_payment_number: null | number;
  tac: null | number;
  max_grace_period: null | number;
  min_grace_period: null | number;
  min_requested_amount: null | number;
  schedule_based_conditions: TRatingsScheduleBasedConditions[];
  created_at: string;
  updated_at: string;
}

export interface ICustomVariablesRequest {
  application: IApplicationCustomVariables[];
  borrower: {
    person: IPersonCustomVariables[];
    business: IBusinessCustomVariables[];
    person_document: [];
    business_document: [];
  };
  guarantors: {
    person: [];
    business: [];
    person_document: [];
    business_document: [];
  };
  co_signers: {
    person: [];
    business: [];
    person_document: [];
    business_document: [];
  };
  employer: IBusinessCustomVariables[];
  employer_document: [];
}

export interface IFunding {
  id: string;
  legal_name: string;
  taxpayer_id: string;
  phone: {
    country_code: string;
    area_code: string;
    number: string;
    formatted_number: string;
  };
  email_address: string;
  address: {
    street_name: string;
    street_number: number;
    postal_code: string;
    district: string;
    city: string;
    state_code: string;
    country_code: string;
    extra_info: string;
  };
  relations: Array<any>;
  metadata: {
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
  version: number;
  alias: any;
}

export interface ILoadingsInitialState {
  isApplicationLoading: boolean;
  isBasePremiumAmountLoading: boolean;
  isCancelApplicationLoading: boolean;
  isCommercialPaperStatusUpdateLoading: boolean;
  isConsignedLoading: boolean;
  isDataprevPolling: boolean;
  isDownloadAgreementLoading: boolean;
  isDownloadSignedAgreementLoading: boolean;
  isDownloadSignedEndorsementLoading: boolean;
  isDownloadSignedSecuritizationEndorsementLoading: boolean;
  isDownloadUnicoDocumentsLoading: boolean;
  isQualificationStatusOptionsLoading: boolean;
  isResendSignatureLoading: boolean;
  isUpdatePaymentOrderLoading: boolean;
  isUploadCustomCcbTemplateLoading: boolean;
  isSubmitConsigneeLoading: boolean;
  isApplicationGuaranteeLoading: boolean;
}

export enum EGuaranteeStatus {
  WAITING_ANNOTATION_REGISTRATION = 'WAITING_ANNOTATION_REGISTRATION',
  WAITING_CONFIRMATION = 'WAITING_CONFIRMATION',
  APPROVED = 'APPROVED',
  ERROR = 'ERROR',
  CANCELED = 'CANCELED',
}

export enum EGuaranteeOperationStatus {
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  CANCELED = 'CANCELED',
}

export enum EGuaranteeActionType {
  START_ANNOTATION = 'START_ANNOTATION',
}

export type TGuaranteeOperationError = {
  code?: string | null;
  message?: string | null;
};

export type TGuaranteeOperation = {
  status?: EGuaranteeOperationStatus | string | null;
  action_type?: EGuaranteeActionType | string | null;
  error?: TGuaranteeOperationError | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TApplicationGuarantee = {
  status?: EGuaranteeStatus | string | null;
  contract_type?: string | null;
  operations?: TGuaranteeOperation[] | null;
};

export type TApplicationGuaranteeResponse = {
  application_id: string;
  guarantee: TApplicationGuarantee;
};

export enum EPaymentMethod {
  BOLETO = 'BOLETO',
  PIX = 'PIX',
  TED = 'TED',
  BANK_ACCOUNT_DEDUCTION = 'BANK_ACCOUNT_DEDUCTION',
  PAYROLL_DEDUCTION = 'PAYROLL_DEDUCTION',
  RECEIVABLES = 'RECEIVABLES',
}

export const PaymentMethodDescription = {
  [EPaymentMethod.BOLETO]: 'Boleto',
  [EPaymentMethod.PIX]: 'Pix',
  [EPaymentMethod.TED]: 'TED',
  [EPaymentMethod.BANK_ACCOUNT_DEDUCTION]: 'Débito em conta',
  [EPaymentMethod.PAYROLL_DEDUCTION]: 'Consignação em folha de pagamento',
  [EPaymentMethod.RECEIVABLES]: 'Recebíveis',
};

export enum ESignatureCollectMethod {
  NONE = 'NONE',
  SMS = 'SMS',
  LINK = 'LINK',
  EMAIL = 'EMAIL',
  PHYSICAL = 'PHYSICAL',
  WHATSAPP = 'WHATSAPP',
}

export enum EIssuerDisbursementType {
  BENEFICIARY = 'BENEFICIARY',
  ORIGINATOR = 'ORIGINATOR',
}

export interface TQualificationRequestApplication {
  id: string;
  funding: Funding;
  employer: null;
  role: string;
  status: string;
  borrower: TQualificationRequestApplicationBorrower;
  application: TApplicationResponse;
  created_at: string;
  updated_at: string;
}

export interface ProductClass {
  id: string;
}

export type CustomVariables = unknown;

export interface Funding {
  id: string;
  legal_name: string;
}

export interface LoanDetails {
  total_processing_cost: number;
  annual_interest_rate: number;
  annual_effective_interest_rate: number;
  monthly_effective_interest_rate: number;
  total_amount_owed: number;
  disbursement_amount: number;
  financed_amount: number;
  iof_daily_rate: number;
  iof_amount: number;
  iof_base_rate: number;
  payment_amount: number;
  issue_date: string;
  schedule: Schedule[];
  last_payment_date: string;
}

export interface Schedule {
  payment: number;
  iof: number;
  balance: number;
  interest: number;
  principal: number;
  period: number;
  running_day: number;
  payment_date: string;
}

export interface TQualificationRequestApplicationBorrower {
  id: string;
  name: string;
}

export type ApplicationBeneficiaryAccountBaas = {
  account_number: string;
};

export type ApplicationBeneficiaryAccountExternalBankAccount = {
  bank_code: string;
  bank_account: string;
  bank_account_digit: string;
  bank_branch: string;
  bank_account_type: string;
  ispb_code: string;
};

export type ApplicationBeneficiaryAccountHolder = {
  name: string;
  taxpayer_id: string;
};

export type ApplicationBeneficiaryAccountPix = {
  key: string;
  key_type: string;
};

export interface ApplicationBeneficiaryAccount {
  baas: ApplicationBeneficiaryAccountBaas | null;
  external_bank_account: ApplicationBeneficiaryAccountExternalBankAccount | null;
  holder: ApplicationBeneficiaryAccountHolder;
  pix: IPixKey | null;
  registered_account_id: string | null;
}

export type PaymentOrderBilletInfo = {
  digitable: string;
  type: number;
  original_amount: number;
  amount: number;
  due_date: string;
  settle_date: string;
  assignor: string;
  payer: string;
  document_payer: string;
  recipient: string;
  document_recipient: string;
  validation_hash: string;
  discount_amount: string;
  fine_amount: string;
  identifier?: string;
  paid_at?: string;
  payment_authentication_code?: string;
};

export interface PaymentOrder {
  amount: number;
  beneficiary_account: ApplicationBeneficiaryAccount;
  beneficiary_type: string;
  id: string;
  status: string;
  type: string;
  billet_info?: PaymentOrderBilletInfo;
  allow_failure: unknown;
}

export enum EApplicationCessionStatus {
  NOT_CEDED = 'NOT_CEDED',
  ENDORSEMENT_RENDERING = 'ENDORSEMENT_RENDERING',
  ENDORSEMENT_RENDERED = 'ENDORSEMENT_RENDERED',
  ENDORSEMENT_SIGNED = 'ENDORSEMENT_SIGNED',
  CESSION_SIGNED = 'CESSION_SIGNED',
  ENDORSED = 'ENDORSED',
  CEDED = 'CEDED',
  WAITING_ENDORSEMENT_RENDERING = 'WAITING_ENDORSEMENT_RENDERING',
  SECURITIZATION_ENDORSEMENT_SIGNED = 'SECURITIZATION_ENDORSEMENT_SIGNED',
  CESSION_RENDERING = 'CESSION_RENDERING',
  CESSION_SENT_TO_SIGN = 'CESSION_SENT_TO_SIGN',
  SECURITIZATION_ENDORSEMENT_RENDERING = 'SECURITIZATION_ENDORSEMENT_RENDERING',
  SECURITIZATION_ENDORSEMENT_RENDERED = 'SECURITIZATION_ENDORSEMENT_RENDERED',
}

export type TApplicationResponse = {
  id: string;
  product: { id: string };
  borrower: { id: string };
  requested_amount: number;
  interest_rate: number;
  tac_amount: number;
  finance_fee: number;
  num_payments: number;
  first_payment_date: string;
  disbursement_date: string;
  issue_date: string;
  status: string;
  status_description?: string;
  base_premium_amount?: number;
  issue_value?: number;
  additional_premium_amount?: number;
  insurance_amount?: number | null;
  originator?: {
    id: string;
    legal_name: string;
  };
  managed_account_for_issuer_disbursement?: {
    product_configuration: null;
    baas?: {
      account_number: string;
      pix_key: string;
      holder_type: string;
    } | null;
  } | null;
  custodian: TCustodianRequest | null;
  ipoc_number: string | null;
  additional_installment_description: string | null;
  additional_installment_fee: number | null;
  split_beneficiary_account: TApplicationSplitBeneficiaryAccount | null;
  loan_details: {
    total_processing_cost: number;
    annual_interest_rate: number;
    annual_effective_interest_rate: number;
    monthly_effective_interest_rate: number;
    total_amount_owed: number;
    disbursement_amount: number;
    financed_amount: number;
    iof_daily_rate: number;
    iof_amount: number;
    iof_base_rate: number;
    insurance_amount?: number | null;
    payment_amount: number;
    issue_date: string;
    schedule: Array<{
      payment: number;
      iof: number;
      balance: number;
      interest: number;
      principal: number;
      period: number;
      running_day: number;
      payment_date: string;
      additional_values: number | null;
      present_value: number | null;
      status: string | null;
      late_day: number | null;
      paid_value: number | null;
    }>;
    last_payment_date: string;
  };
  custom_variables: { [key: string]: string | null | undefined };
  canceled_at: string | null;
  sequential_id: number;
  beneficiary_account?:
    | (TBeneficiaryAccount & { registered_account_id: string })
    | null;
  funding?: {
    id?: string;
    legal_name?: string;
    alias?: string;
  };
  payment_method: EPaymentMethod;
  backoffice_import_status: string;
  guarantors: Array<{ guarantor_id: string; type: 'PERSON' | 'BUSINESS' }>;
  co_signers: Array<{ co_signer_id: string; type: 'PERSON' | 'BUSINESS' }>;
  co_debtors: Array<{ co_debtor_id: string; type: 'PERSON' | 'BUSINESS' }>;
  investors: Array<{ investor_id: string; type: 'PERSON' | 'BUSINESS' }>;
  sureties: Array<{ surety_id: string; type: 'PERSON' | 'BUSINESS' }>;
  collateral_providers: Array<{
    collateral_provider_id: string;
    type: 'PERSON' | 'BUSINESS';
  }>;
  representatives: Array<{
    representative_id: string;
    type: 'PERSON' | 'BUSINESS';
  }>;

  signature_collect_method: ESignatureCollectMethod;
  issuer_disbursement_type: EIssuerDisbursementType;
  disbursement?: {
    processed_at: string;
    receipt: string;
    requested_at: string;
  };
  signature_collect_options: {
    require_self_photo: boolean;
    require_document_photo: boolean;
    require_selfie_validation: boolean;
  };
  loan: null;
  created_at: string;
  payment_orders: PaymentOrder[];
  cession_status: EApplicationCessionStatus;
};
